package com.trevorwiebe.timesheet.settlements.domain.model

import com.trevorwiebe.timesheet.core.domain.formatCentsAsDollars
import kotlinx.datetime.LocalDate
import kotlin.math.roundToInt

enum class SettlementStatus { DRAFT, SENT }

/**
 * One load on a settlement. The pay percentages are set when the settlement is created and can be
 * changed per load on the draft. A draft's charges follow later edits to its loads; a sent
 * settlement never changes. Money is whole cents.
 */
data class SettlementLine(
    val loadId: String,
    val loadNumber: String,
    val date: LocalDate,
    val driverId: String,
    val driverName: String,
    val pickup: String,
    val drop: String,
    val base: Int,
    val fuelSurcharge: Int = 0,
    val detention: Int = 0,
    val stopOff: Int = 0,
    val other: Int = 0,
    val basePayPercent: Double,
    val fuelSurchargePayPercent: Double = 100.0,
    val detentionPayPercent: Double = 100.0,
    val stopOffPayPercent: Double = 100.0,
    val otherPayPercent: Double = 100.0,
    // "25.00 tons @ $34.00" for a per-unit rate; blank for a flat rate.
    val unitDetail: String = "",
) {
    val extras: Int get() = fuelSurcharge + detention + stopOff + other
    val gross: Int get() = base + extras
    val basePay: Int get() = (base * basePayPercent / 100.0).roundToInt()
    val extraPay: Int get() = charges.sumOf { it.pay }
    val pay: Int get() = basePay + extraPay

    val grossFormatted: String get() = "$" + gross.formatCentsAsDollars()
    val payFormatted: String get() = "$" + pay.formatCentsAsDollars()

    /** The extra charges this load has, each with its own pay percentage. */
    val charges: List<SettlementChargePay>
        get() = listOf(
            SettlementChargePay(SettlementCharge.FUEL_SURCHARGE, fuelSurcharge, fuelSurchargePayPercent),
            SettlementChargePay(SettlementCharge.DETENTION, detention, detentionPayPercent),
            SettlementChargePay(SettlementCharge.STOP_OFF, stopOff, stopOffPayPercent),
            SettlementChargePay(SettlementCharge.OTHER, other, otherPayPercent),
        ).filter { it.amount != 0 }

    fun withPayPercent(charge: SettlementCharge, percent: Double): SettlementLine = when (charge) {
        SettlementCharge.FUEL_SURCHARGE -> copy(fuelSurchargePayPercent = percent)
        SettlementCharge.DETENTION -> copy(detentionPayPercent = percent)
        SettlementCharge.STOP_OFF -> copy(stopOffPayPercent = percent)
        SettlementCharge.OTHER -> copy(otherPayPercent = percent)
    }
}

enum class SettlementCharge(val label: String) {
    FUEL_SURCHARGE("Fuel surcharge"),
    DETENTION("Detention"),
    STOP_OFF("Stop-off"),
    OTHER("Other"),
}

data class SettlementChargePay(val charge: SettlementCharge, val amount: Int, val payPercent: Double) {
    val pay: Int get() = (amount * payPercent / 100.0).roundToInt()
    val amountFormatted: String get() = "$" + amount.formatCentsAsDollars()
    val payFormatted: String get() = "$" + pay.formatCentsAsDollars()
}

data class SettlementDeduction(val description: String, val amount: Int) {
    val amountFormatted: String get() = "$" + amount.formatCentsAsDollars()
}

data class Settlement(
    val id: String,
    val number: Int,
    val ownerOperatorId: String,
    // Who's paid: the owner operator's name, or the carrier's on a carrier settlement.
    val ownerOperatorName: String,
    val settlementDate: LocalDate,
    val lines: List<SettlementLine>,
    val deductions: List<SettlementDeduction> = emptyList(),
    // Mileage-log miles driven by the owner operator's drivers over the settlement's load dates.
    val miles: Long = 0,
    val status: SettlementStatus = SettlementStatus.DRAFT,
    val sentAt: LocalDate? = null,
    val sentTo: String = "",
    val pdfPath: String = "",
    // Set instead of ownerOperatorId when an outside carrier hauled the loads.
    val carrierId: String = "",
) {
    val isCarrier: Boolean get() = carrierId.isNotEmpty()
    val loadIds: List<String> get() = lines.map { it.loadId }
    val isDraft: Boolean get() = status == SettlementStatus.DRAFT
    val numberLabel: String get() = "#$number"

    val periodStart: LocalDate? get() = lines.minOfOrNull { it.date }
    val periodEnd: LocalDate? get() = lines.maxOfOrNull { it.date }

    val gross: Int get() = lines.sumOf { it.gross }
    val pay: Int get() = lines.sumOf { it.pay }
    val deductionsTotal: Int get() = deductions.sumOf { it.amount }
    val netPay: Int get() = pay - deductionsTotal

    val grossFormatted: String get() = "$" + gross.formatCentsAsDollars()
    val payFormatted: String get() = "$" + pay.formatCentsAsDollars()
    val deductionsTotalFormatted: String get() = "$" + deductionsTotal.formatCentsAsDollars()
    val netPayFormatted: String get() = "$" + netPay.formatCentsAsDollars()
    val grossPerMileFormatted: String get() = perMile(gross, miles)
    val payPerMileFormatted: String get() = perMile(pay, miles)

    val statusLabel: String get() = if (isDraft) "Draft" else "Sent"
}

private fun perMile(cents: Int, miles: Long): String {
    if (miles <= 0) return "—"
    val centsPerMile = (cents.toDouble() / miles).roundToInt()
    return "$" + centsPerMile.formatCentsAsDollars()
}
